/**
 * HighlightedText - Highlight matching substring in search results
 *
 * Case-insensitive, highlights first match only.
 * Uses subtle ModCar-style highlight (not bright yellow).
 */

interface HighlightedTextProps {
  /** The full text to display */
  text: string;
  /** The query to highlight (will be escaped for regex safety) */
  query: string;
  /** Additional class name for the container */
  className?: string;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function HighlightedText({ text, query, className }: HighlightedTextProps) {
  if (!query || !text) {
    return <span className={className}>{text}</span>;
  }

  // Escape the query for safe regex use
  const escapedQuery = escapeRegex(query.trim());

  if (!escapedQuery) {
    return <span className={className}>{text}</span>;
  }

  // Find first match (case-insensitive)
  const regex = new RegExp(`(${escapedQuery})`, 'i');
  const match = text.match(regex);

  if (!match || match.index === undefined) {
    return <span className={className}>{text}</span>;
  }

  const beforeMatch = text.slice(0, match.index);
  const matchedText = text.slice(match.index, match.index + match[0].length);
  const afterMatch = text.slice(match.index + match[0].length);

  return (
    <span className={className}>
      {beforeMatch}
      <mark className="bg-primary/20 text-foreground rounded-sm px-0.5 font-medium">
        {matchedText}
      </mark>
      {afterMatch}
    </span>
  );
}

export default HighlightedText;
